import AppRoutes from "AppRoutes";
import { createLogger } from "logging";
import { WorkingDocumentModel } from "models/WorkingDocumentModel";
import { useLayout, useTranslations } from "providers";
import { useDocument } from "providers";
import { useDocumentsPanel } from "providers";
import React, { PropsWithChildren } from "react";
import { Item, Menu, TriggerEvent, useContextMenu } from "react-contexify";

import { DocumentsContextMenuContext } from "./DocumentsContextMenuContext";

const menuId = "documents-context-menu";

const logger = createLogger("DocumentsContextMenuProvider");

export const DocumentsContextMenuProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const i18n = useTranslations();
  const { setSelectDocumentId, setConfirmDeleteDocument } = useDocument();
  const { openDocumentsPanel } = useDocumentsPanel();
  const { show } = useContextMenu({ id: menuId });
  const { handleNavClick } = useLayout();

  function onClickDelete(item: WorkingDocumentModel) {
    logger.debug("onClickDelete(): ", item);
    setConfirmDeleteDocument({ id: item.id, title: item.fileName });
  }

  function showContextMenu(event: TriggerEvent, item: WorkingDocumentModel) {
    logger.debug("showContextMenu(): ", item);
    setSelectDocumentId(item.id);
    show({ event, id: menuId, props: item });
  }

  function onClickViewHistory(item: WorkingDocumentModel): void {
    logger.debug("onClickViewHistory(): ", item);
    const url = AppRoutes.toBackendAuditLog("Document", item.id.toString());
    handleNavClick(url);
  }

  function onClickEdit(item: WorkingDocumentModel): void {
    const agendaItemId = item.agendaItem?.id;
    logger.debug("onClickEdit(): ", item, agendaItemId);
    if (!agendaItemId) return;
    openDocumentsPanel(agendaItemId);
  }

  function isDisabledEdit(item: WorkingDocumentModel): boolean {
    return !item.canEdit;
  }

  function isDisabledViewHistory(item: WorkingDocumentModel) {
    if (!item) return true;
    return !item.canViewHistory;
  }

  function isDisabledDelete(item: WorkingDocumentModel) {
    if (!item) return true;
    return !item.canDelete;
  }

  return (
    <DocumentsContextMenuContext.Provider
      value={{
        showContextMenu,
      }}
    >
      <Menu id={menuId} theme="sbb">
        <Item onClick={(e) => onClickEdit(e.props)} disabled={(e) => isDisabledEdit(e.props)}>
          {i18n.Actions_View_Documents}
        </Item>
        <Item onClick={(e) => onClickDelete(e.props)} disabled={(e) => isDisabledDelete(e.props)}>
          {i18n.Actions_Delete_Document}
        </Item>
        <Item onClick={(e) => onClickViewHistory(e.props)} disabled={(e) => isDisabledViewHistory(e.props)}>
          {i18n.Actions_View_History}
        </Item>
      </Menu>

      {children}
    </DocumentsContextMenuContext.Provider>
  );
};
